/**
 * CrossSiteCallout — Passerelle réciproque vers codevoiture.fr.
 *
 * Créé en juin 2026, en miroir du callout que codevoiture.fr renvoie vers
 * code2roues.fr. Objectif : signaler aux visiteurs qui préparent aussi
 * le permis B qu'il existe une plateforme sœur (même équipe ECSR, même
 * ADN éditorial magazine).
 *
 * Design :
 *  • Deux colonnes équilibrées : identité actuelle (moto) ↔ identité sœur (voiture)
 *  • Séparateur central « ↔ » sur desktop, empilé sur mobile
 *  • Charte respectée : Playfair Display pour titres, accent primaire discret
 *  • Compatible dark/light mode via variables CSS hsl
 *  • Aucune bannière rouge agressive : ton institutionnel « famille de sites »
 */
import React from 'react';
import { Bike, Car, ArrowUpRight, Sparkles } from 'lucide-react';

export default function CrossSiteCallout() {
  return (
    <section
      className="relative overflow-hidden rounded-2xl border-2 border-border bg-card"
      data-testid="home-cross-site-callout"
      aria-labelledby="cross-site-title"
      data-reveal=""
    >
      {/* Filet éditorial haut — bicolore rouge/bleu, signature de la famille */}
      <div className="absolute inset-x-0 top-0 h-[3px] flex" aria-hidden="true">
        <span className="flex-1" style={{ background: 'hsl(var(--primary))' }} />
        <span className="flex-1" style={{ background: '#1d4ed8' }} />
      </div>

      {/* Kicker en haut, centré */}
      <div className="px-5 sm:px-8 pt-6 sm:pt-8 pb-2 text-center">
        <span className="inline-flex items-center gap-1.5 text-[10px] sm:text-[11px] font-black uppercase tracking-[0.22em] text-foreground/55">
          <Sparkles className="w-3 h-3 text-[hsl(var(--primary))]" />
          Notre plateforme sœur
          <Sparkles className="w-3 h-3 text-[#1d4ed8]" />
        </span>
      </div>

      {/* Titre principal */}
      <h2
        id="cross-site-title"
        className="text-center text-2xl sm:text-3xl lg:text-[2.15rem] font-black tracking-tight leading-[1.1] px-5 sm:px-8 text-foreground"
        style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
      >
        Vous préparez aussi le permis B&nbsp;?
      </h2>
      <p className="text-center text-sm sm:text-base text-foreground/70 leading-relaxed max-w-2xl mx-auto px-5 sm:px-8 mt-3 mb-6 sm:mb-8">
        Même équipe ECSR, même ADN magazine — appliqué au code voiture. Une
        seule pédagogie, deux permis, zéro pub.
      </p>

      {/* Deux colonnes identités : Moto (actuelle) ↔ Voiture (sœur) */}
      <div className="px-5 sm:px-8 pb-6 sm:pb-8">
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-4 sm:gap-3 items-stretch">
          {/* Colonne gauche : Code 2 Roues (identité actuelle) */}
          <div
            className="rounded-xl border border-border/60 bg-background p-4 sm:p-5 flex flex-col items-center text-center"
            data-testid="cross-site-current"
          >
            <div
              className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center text-white mb-3 flex-shrink-0"
              style={{ background: 'hsl(var(--primary))' }}
              aria-hidden="true"
            >
              <Bike className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <div className="text-[9px] sm:text-[10px] font-black uppercase tracking-[0.18em] text-[hsl(var(--primary))] mb-1">
              Vous êtes ici
            </div>
            <div
              className="text-base sm:text-lg font-black tracking-tight leading-tight text-foreground"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              Code 2 Roues
            </div>
            <div className="text-[11px] sm:text-xs text-foreground/60 mt-1 leading-snug">
              AM · A1 · A2 · Passerelle A · 125&nbsp;(B) · L5e
            </div>
          </div>

          {/* Séparateur central — mobile stack (↕), desktop arrow (↔) */}
          <div
            className="flex sm:flex-col items-center justify-center py-1 sm:py-0"
            aria-hidden="true"
          >
            <div
              className="w-8 h-8 sm:w-10 sm:h-10 rounded-full grid place-items-center bg-muted text-foreground/50"
              style={{ boxShadow: '0 2px 8px -4px rgba(0,0,0,0.2)' }}
            >
              <span className="hidden sm:inline text-lg font-black">↔</span>
              <span className="sm:hidden text-lg font-black">↕</span>
            </div>
          </div>

          {/* Colonne droite : CodeVoiture.fr (identité sœur) — cliquable */}
          <a
            href="https://codevoiture.fr"
            target="_blank"
            rel="noopener noreferrer"
            className="group rounded-xl border-2 border-[#1d4ed8]/40 bg-background hover:bg-[#1d4ed8]/[0.03] hover:border-[#1d4ed8] transition-all p-4 sm:p-5 flex flex-col items-center text-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1d4ed8]"
            data-testid="cross-site-partner"
            aria-label="Découvrir CodeVoiture.fr — plateforme sœur pour le permis voiture (ouvre dans un nouvel onglet)"
          >
            <div
              className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center text-white mb-3 flex-shrink-0 group-hover:scale-105 transition-transform"
              style={{ background: '#1d4ed8' }}
              aria-hidden="true"
            >
              <Car className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <div className="text-[9px] sm:text-[10px] font-black uppercase tracking-[0.18em] text-[#1d4ed8] mb-1 inline-flex items-center gap-1">
              Plateforme sœur
              <ArrowUpRight className="w-3 h-3" />
            </div>
            <div
              className="text-base sm:text-lg font-black tracking-tight leading-tight text-foreground"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              CodeVoiture.fr
            </div>
            <div className="text-[11px] sm:text-xs text-foreground/60 mt-1 leading-snug">
              B · AAC · CS · B96 · B+E · BEA
            </div>
          </a>
        </div>

        {/* Preuves communes (ADN partagé) */}
        <ul className="mt-5 sm:mt-6 flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-[11px] sm:text-xs text-foreground/55">
          <li className="inline-flex items-center gap-1.5">
            <span className="w-1 h-1 rounded-full bg-foreground/30" aria-hidden="true" />
            100&nbsp;% gratuit
          </li>
          <li className="inline-flex items-center gap-1.5">
            <span className="w-1 h-1 rounded-full bg-foreground/30" aria-hidden="true" />
            Conçu par des moniteurs ECSR
          </li>
          <li className="inline-flex items-center gap-1.5">
            <span className="w-1 h-1 rounded-full bg-foreground/30" aria-hidden="true" />
            Sans pub · sans tracking
          </li>
          <li className="inline-flex items-center gap-1.5">
            <span className="w-1 h-1 rounded-full bg-foreground/30" aria-hidden="true" />
            Format magazine
          </li>
        </ul>

        {/* CTA principal — bouton mobile-friendly */}
        <div className="mt-5 sm:mt-6 flex justify-center">
          <a
            href="https://codevoiture.fr"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 sm:px-6 h-11 sm:h-12 rounded-full text-sm sm:text-base font-bold text-white hover:opacity-95 active:scale-[0.98] transition-all shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#1d4ed8]"
            style={{ background: '#1d4ed8' }}
            data-testid="cross-site-cta"
          >
            <Car className="w-4 h-4" />
            Découvrir CodeVoiture.fr
            <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
}
